// محرك المحاكاة الكهربائية
import { SPECS, type Rail } from "./catalog";

export interface CompState {
  closed?: boolean; // مفتاح/قاطع مغلق (مفعّل)
  pressed?: boolean; // زر مضغوط (لحظي)
  tripped?: boolean; // فاصل
  elapsed?: number; // زمن التايمر المنقضي (مللي ثانية)
  delay?: number; // زمن التايمر بالمللي ثانية
  pos?: number; // موضع مفتاح الاختيار
  linkTo?: string; // معرّف العنصر المتبوع (للنقاط المساعدة)
  coilVoltage?: string;

  // مصادر التغذية القابلة للتعديل
  sourceVoltage?: number;

  // إعدادات وحالة أجهزة الحماية
  minVoltage?: number;
  maxVoltage?: number;
  protectionDelay?: number; // ms
  protectionElapsed?: number; // ms
  protectionActive?: boolean; // خرج OUT بعد اكتمال الزمن والحالة السليمة
  protectionInputPowered?: boolean; // لمبة ON
  protectionFault?: boolean;
  measuredVoltage?: number;
}

export interface Comp {
  id: string;
  type: string;
  x: number;
  y: number;
  rot: number;
  label: string;
  state: CompState;
}


export type WireKind = "phase" | "neutral" | "earth" | "control";

export interface Wire {
  id: string;
  from: { c: string; t: string };
  to: { c: string; t: string };
  kind: WireKind;
  color?: string; // لون مخصص يختاره المستخدم
  mid?: { x: number; y: number }; // نقطة توجيه السلك
}


export interface Issue {
  level: "error" | "warn" | "info";
  compId?: string;
  message: string;
}

export type MotorDirection = "forward" | "reverse";
export type MotorConnection = "star" | "delta";

export interface ProtectionStatus {
  inputPowered: boolean;
  healthy: boolean;
  fault: boolean;
  voltage: number;
  phaseOk: boolean;
  sequenceOk: boolean;
}

export interface SimResult {
  nodeOf: Map<string, string>;
  railsOf: Map<string, Set<Rail>>;
  energized: Set<string>; // أجهزة ملفها يعمل (بعد التأخير)
  coilPowered: Set<string>; // ملف تصله تغذية الآن
  running: Set<string>; // أحمال تعمل
  liveTerminals: Set<string>;
  liveNodes: Set<string>;

  // حالة الحمايات محسوبة من التوصيلات والفولتية الفعلية للمصدر
  protectionStatus: Map<string, ProtectionStatus>;

  // اتجاه دوران المحركات الثلاثية حسب ترتيب الفازات
  motorDirection: Map<string, MotorDirection>;

  // نوع ربط المحرك ذي الستة أطراف
  motorConnection: Map<string, MotorConnection>;

  issues: Issue[];
}

const key = (c: string, t: string) => `${c}:${t}`;
const comp0Link = (c: Comp) => c.state.linkTo;

class DSU {
  p = new Map<string, string>();
  find(x: string): string {
    const px = this.p.get(x);
    if (px === undefined) {
      this.p.set(x, x);
      return x;
    }
    if (px === x) return x;
    const r = this.find(px);
    this.p.set(x, r);
    return r;
  }
  union(a: string, b: string) {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.p.set(ra, rb);
  }
}

const HOT: Rail[] = ["L1", "L2", "L3", "C+"];
const PHASES: Rail[] = ["L1", "L2", "L3"];

function hasAny(s: Set<Rail> | undefined, list: Rail[]) {
  if (!s) return false;
  return list.some((r) => s.has(r));
}

function pairPoweredAC(a?: Set<Rail>, b?: Set<Rail>) {
  if (!a || !b) return false;

  // AC أحادي الطور يعمل فقط بين فازة واحدة + N.
  // لا يقبل فازتين ولا يقبل C+/C-.
  const aPhases = PHASES.filter((p) => a.has(p));
  const bPhases = PHASES.filter((p) => b.has(p));

  const aCleanPhase =
    aPhases.length === 1 &&
    !a.has("N") &&
    !a.has("C+") &&
    !a.has("C-");
  const bCleanPhase =
    bPhases.length === 1 &&
    !b.has("N") &&
    !b.has("C+") &&
    !b.has("C-");

  const aCleanNeutral =
    a.has("N") &&
    aPhases.length === 0 &&
    !a.has("C+") &&
    !a.has("C-");
  const bCleanNeutral =
    b.has("N") &&
    bPhases.length === 0 &&
    !b.has("C+") &&
    !b.has("C-");

  return (
    (aCleanPhase && bCleanNeutral) ||
    (bCleanPhase && aCleanNeutral)
  );
}

function dcDirectionFromPair(
  a?: Set<Rail>,
  b?: Set<Rail>,
): MotorDirection | undefined {
  if (!a || !b) return undefined;

  const aCleanPlus =
    a.has("C+") &&
    !a.has("C-") &&
    !hasAny(a, PHASES) &&
    !a.has("N");
  const aCleanMinus =
    a.has("C-") &&
    !a.has("C+") &&
    !hasAny(a, PHASES) &&
    !a.has("N");
  const bCleanPlus =
    b.has("C+") &&
    !b.has("C-") &&
    !hasAny(b, PHASES) &&
    !b.has("N");
  const bCleanMinus =
    b.has("C-") &&
    !b.has("C+") &&
    !hasAny(b, PHASES) &&
    !b.has("N");

  if (aCleanPlus && bCleanMinus) return "forward";
  if (aCleanMinus && bCleanPlus) return "reverse";
  return undefined;
}

function pairPoweredDC(a?: Set<Rail>, b?: Set<Rail>) {
  return dcDirectionFromPair(a, b) !== undefined;
}

/** ترجع فازة واحدة فقط إذا كانت العقدة تحمل فازة واحدة نظيفة */
function singlePhase(s?: Set<Rail>): Rail | undefined {
  if (!s) return undefined;
  const found = PHASES.filter((p) => s.has(p));
  return found.length === 1 ? found[0] : undefined;
}

/**
 * اتجاه دوران محرك 3 فاز حسب ترتيب الفازات على U/V/W.
 * L1-L2-L3 وأي تدوير دوري لها = Forward
 * تبديل أي فازتين = Reverse
 */
function directionFromThreePhase(
  a?: Set<Rail>,
  b?: Set<Rail>,
  c?: Set<Rail>,
): MotorDirection | undefined {
  const p1 = singlePhase(a);
  const p2 = singlePhase(b);
  const p3 = singlePhase(c);

  if (!p1 || !p2 || !p3) return undefined;
  if (new Set([p1, p2, p3]).size !== 3) return undefined;

  const seq = `${p1}-${p2}-${p3}`;

  const forward = new Set([
    "L1-L2-L3",
    "L2-L3-L1",
    "L3-L1-L2",
  ]);

  return forward.has(seq) ? "forward" : "reverse";
}

export function isPoleClosed(
  comp: Comp,
  pole: { type: "no" | "nc"; pos?: number[] },
  actuated: boolean,
): boolean {
  // نقاط مفتاح الاختيار تعتمد على الموضع الحالي فقط
  if (pole.pos) {
    const at = pole.pos.includes(comp.state.pos ?? 0);
    return pole.type === "no" ? at : !at;
  }
  return pole.type === "no" ? actuated : !actuated;
}

/** هل العنصر (كونتكتر/ريليه/تايمر) في حالة عمل الآن؟ */
export function actuatedOf(
  comp: Comp,
  energized: Set<string>,
  masterOf?: (id: string) => Comp | undefined,
): boolean {
  const spec = SPECS[comp.type];

  // أجهزة الحماية الجديدة لا تُشغّل نقاطها بالنقر اليدوي؛
  // خرجها يعتمد على الفولتية/الفازات والزمن المحسوب في المحاكي.
  if (
    comp.type === "phase_failure" ||
    comp.type === "voltage_monitor"
  ) {
    return !!comp.state.protectionActive;
  }

  switch (spec?.actuator) {
    case "coil":
      return energized.has(comp.id);
    case "press":
      return !!comp.state.pressed;
    case "manual":
      return !!comp.state.closed;
    case "select":
      return (comp.state.pos ?? 0) > 0;
    case "trip":
      return !!comp.state.tripped;
    case "link": {
      // نقطة مساعدة تتبع حالة ملف عنصر آخر
      const id = comp.state.linkTo;
      if (!id) return false;
      const m = masterOf?.(id);
      if (!m) return false;
      return actuatedOf(m, energized, masterOf);
    }
    default:
      return false;
  }
}

export function simulate(
  comps: Comp[],
  wires: Wire[],
  prevEnergized?: Set<string>,
): SimResult {
  // البداية من الحالة السابقة حتى تعمل دوائر التثبيت الذاتي (Self Holding)
  const energized = new Set<string>(prevEnergized ?? []);
  const before = new Set<string>(prevEnergized ?? []);
  const byId = new Map(comps.map((c) => [c.id, c]));
  const byLabel = new Map(comps.map((c) => [c.label, c]));
  const masterOf = (id: string) => byId.get(id) ?? byLabel.get(id);
  let coilPowered = new Set<string>();
  let dsu = new DSU();
  let railsOf = new Map<string, Set<Rail>>();

  for (let iter = 0; iter < 16; iter++) {
    dsu = new DSU();
    for (const c of comps) {
      const spec = SPECS[c.type];
      if (!spec) continue;
      for (const t of spec.terminals) dsu.find(key(c.id, t.id));
      for (const l of spec.links ?? []) dsu.union(key(c.id, l[0]), key(c.id, l[1]));
      const act = actuatedOf(c, energized, masterOf);
      for (const p of spec.poles ?? []) {
        if (isPoleClosed(c, p, act)) dsu.union(key(c.id, p.a), key(c.id, p.b));
      }
    }
    for (const w of wires)
      dsu.union(key(w.from.c, w.from.t), key(w.to.c, w.to.t));

    railsOf = new Map();
    for (const c of comps) {
      const spec = SPECS[c.type];
      for (const r of spec?.rails ?? []) {
        const root = dsu.find(key(c.id, r.t));
        const set = railsOf.get(root) ?? new Set<Rail>();
        set.add(r.rail);
        railsOf.set(root, set);
      }
    }

    const nextPowered = new Set<string>();
    for (const c of comps) {
      const spec = SPECS[c.type];
      if (!spec?.coil) continue;
      const a = railsOf.get(dsu.find(key(c.id, spec.coil[0])));
      const b = railsOf.get(dsu.find(key(c.id, spec.coil[1])));

      // ملف 24V DC يعمل فقط على C+/C-.
      // بقية الملفات الأحادية تعمل فقط على فازة + N.
      const isDcCoil = (spec.coilVoltage ?? "").toUpperCase().includes("DC");
      const powered = isDcCoil
        ? pairPoweredDC(a, b)
        : pairPoweredAC(a, b);

      if (powered) nextPowered.add(c.id);
    }

    const nextEnergized = new Set<string>();
    for (const c of comps) {
      const spec = SPECS[c.type];
      if (!spec?.coil) continue;
      const powered = nextPowered.has(c.id);
      const delay = c.state.delay ?? (spec.delay !== undefined ? spec.delay * 1000 : undefined);
      const elapsed = c.state.elapsed ?? 0;
      if (spec.timerMode === "off" && delay !== undefined) {
        // تأخير فصل: يعمل فوراً عند التغذية ويستمر بعد قطعها حتى انتهاء الزمن
        if (powered) nextEnergized.add(c.id);
        else if (before.has(c.id) && elapsed < delay) nextEnergized.add(c.id);
        continue;
      }
      if (!powered) continue;
      if (delay !== undefined) {
        if (elapsed >= delay) nextEnergized.add(c.id);
      } else nextEnergized.add(c.id);
    }


    const same =
      nextEnergized.size === energized.size &&
      [...nextEnergized].every((x) => energized.has(x));
    energized.clear();
    nextEnergized.forEach((x) => energized.add(x));
    coilPowered = nextPowered;
    if (same && iter > 0) break;
  }

  // =====================================================
  // الفولتية الفعلية للمصادر وحالة أجهزة الحماية
  // =====================================================
  // نخزن فولتية الفاز-إلى-نيوترال على كل عقدة.
  // للمصدر الثلاثي يدخل المستخدم فولتية خط-إلى-خط (مثلاً 400V)
  // ونحوّلها داخلياً إلى فاز-إلى-نيوترال حتى تنتقل عبر الأسلاك.
  const rootVoltages = new Map<string, number[]>();
  const addRootVoltage = (root: string, value: number) => {
    if (!Number.isFinite(value) || value < 0) return;
    const arr = rootVoltages.get(root) ?? [];
    arr.push(value);
    rootVoltages.set(root, arr);
  };

  for (const c of comps) {
    const spec = SPECS[c.type];
    if (!spec) continue;

    if (c.type === "src_3ph") {
      const lineVoltage = c.state.sourceVoltage ?? spec.sourceVoltage ?? 400;
      const phaseVoltage = lineVoltage / Math.sqrt(3);
      for (const t of ["L1", "L2", "L3"]) {
        addRootVoltage(dsu.find(key(c.id, t)), phaseVoltage);
      }
    } else if (c.type === "src_1ph") {
      const phaseVoltage = c.state.sourceVoltage ?? spec.sourceVoltage ?? 230;
      addRootVoltage(dsu.find(key(c.id, "L")), phaseVoltage);
    } else if (c.type === "src_ctrl_220") {
      addRootVoltage(dsu.find(key(c.id, "L")), 230);
    }
  }

  const voltageAtRoot = (root: string) => {
    const values = rootVoltages.get(root) ?? [];
    if (!values.length) return 0;
    return values.reduce((a, b) => a + b, 0) / values.length;
  };

  const railsAt = (c: Comp, t: string) =>
    railsOf.get(dsu.find(key(c.id, t)));
  const voltageAt = (c: Comp, t: string) =>
    voltageAtRoot(dsu.find(key(c.id, t)));

  const protectionStatus = new Map<string, ProtectionStatus>();

  for (const c of comps) {
    const spec = SPECS[c.type];
    if (!spec) continue;

    const minVoltage =
      c.state.minVoltage ?? spec.protectionMinVoltage ??
      (c.type === "voltage_monitor" ? 190 : 340);
    const maxVoltage =
      c.state.maxVoltage ?? spec.protectionMaxVoltage ??
      (c.type === "voltage_monitor" ? 250 : 440);

    if (c.type === "phase_failure") {
      const t1 = railsAt(c, "L1");
      const t2 = railsAt(c, "L2");
      const t3 = railsAt(c, "L3");
      const p1 = singlePhase(t1);
      const p2 = singlePhase(t2);
      const p3 = singlePhase(t3);
      const phaseOk =
        !!p1 && !!p2 && !!p3 && new Set([p1, p2, p3]).size === 3;

      const v1 = voltageAt(c, "L1");
      const v2 = voltageAt(c, "L2");
      const v3 = voltageAt(c, "L3");
      const presentVoltages = [v1, v2, v3].filter((v) => v > 0);
      const lineVoltage = presentVoltages.length
        ? (presentVoltages.reduce((a, b) => a + b, 0) / presentVoltages.length) *
          Math.sqrt(3)
        : 0;

      const hasNeutral = !!railsAt(c, "N")?.has("N");
      // لمبة ON تمثل وجود تغذية إلكترونية على الجهاز. في الفيس فيلر
      // تبقى ON ما دام النيوترال ومعه فاز واحد على الأقل موجودين.
      const inputPowered =
        hasNeutral && presentVoltages.length > 0;

      const sequenceOk = true;

      const healthy =
        inputPowered &&
        phaseOk &&
        lineVoltage >= minVoltage &&
        lineVoltage <= maxVoltage;

      protectionStatus.set(c.id, {
        inputPowered,
        healthy,
        fault: inputPowered && !healthy,
        voltage: lineVoltage,
        phaseOk,
        sequenceOk,
      });
      continue;
    }

    if (c.type === "voltage_monitor") {
      const lRails = railsAt(c, "L");
      const nRails = railsAt(c, "N");
      const voltage = voltageAt(c, "L");
      const phasePresent = !!singlePhase(lRails);
      const neutralPresent = !!nRails?.has("N");
      const inputPowered = phasePresent && neutralPresent && voltage > 0;
      const healthy =
        inputPowered && voltage >= minVoltage && voltage <= maxVoltage;

      protectionStatus.set(c.id, {
        inputPowered,
        healthy,
        fault: inputPowered && !healthy,
        voltage,
        phaseOk: phasePresent && neutralPresent,
        sequenceOk: true,
      });
    }
  }

  // العقد الحية والأحمال والأخطاء
  const liveNodes = new Set<string>();
  railsOf.forEach((set, node) => {
    if (hasAny(set, HOT) || set.has("N")) liveNodes.add(node);
  });
  const liveTerminals = new Set<string>();
  for (const c of comps) {
    const spec = SPECS[c.type];
    for (const t of spec?.terminals ?? []) {
      const root = dsu.find(key(c.id, t.id));
      if (hasAny(railsOf.get(root), HOT)) liveTerminals.add(key(c.id, t.id));
    }
  }

  const issues: Issue[] = [];
  const running = new Set<string>();
  const motorDirection = new Map<string, MotorDirection>();
  const motorConnection = new Map<string, MotorConnection>();

  // قصر الدائرة
  railsOf.forEach((set, node) => {
    const ph = PHASES.filter((p) => set.has(p));
    if (ph.length > 1 || (ph.length === 1 && set.has("N")) || (set.has("C+") && set.has("C-"))) {
      const owner = comps.find((c) =>
        (SPECS[c.type]?.terminals ?? []).some(
          (t) => dsu.find(key(c.id, t.id)) === node,
        ),
      );
      issues.push({
        level: "error",
        ...(owner ? { compId: owner.id } : {}),
        message: "قصر كهربائي (Short Circuit): تم توصيل مصدرين مختلفين مباشرة بنفس النقطة دون حمل.",
      });
    }
  });

  const hasSource = comps.some((c) => SPECS[c.type]?.rails);
  if (comps.length && !hasSource)
    issues.push({ level: "warn", message: "لا يوجد مصدر تغذية في المخطط. أضف مصدر تغذية أولاً." });

  const connected = new Set<string>();
  for (const w of wires) {
    connected.add(key(w.from.c, w.from.t));
    connected.add(key(w.to.c, w.to.t));
  }

  for (const c of comps) {
    const spec = SPECS[c.type];
    if (!spec) continue;

    // أطراف غير مربوطة
    if (spec.coil) {
      const un = spec.coil.filter((t) => !connected.has(key(c.id, t)));
      if (un.length)
        issues.push({
          level: "warn",
          compId: c.id,
          message: `${c.label}: طرف الملف ${un.join(" و ")} غير مربوط.`,
        });
      else if (!coilPowered.has(c.id))
        issues.push({
          level: "info",
          compId: c.id,
          message: `${c.label}: الملف لا تصله تغذية كاملة (تحقق من مسار الفازة والنيوترال عبر النقاط).`,
        });
    }

    if (spec.linkable && !comp0Link(c)) {
      issues.push({
        level: "warn",
        compId: c.id,
        message: `${c.label}: نقطة مساعدة غير مرتبطة بأي عنصر – افتح الخصائص واختر الكونتكتر أو الريليه التابعة له.`,
      });
    }

    if (spec.load) {
      // =====================================================
      // محرك 3 فاز بستة أطراف - STAR / DELTA
      // =====================================================
      if (c.type === "motor_3ph_6t") {
        const motorTerms = ["U1", "V1", "W1", "U2", "V2", "W2"];

        const un = motorTerms.filter((t) => !connected.has(key(c.id, t)));
        if (un.length)
          issues.push({
            level: "warn",
            compId: c.id,
            message: `${c.label}: أطراف المحرك ${un.join(" ، ")} غير مربوطة.`,
          });

        const rootOf = (t: string) => dsu.find(key(c.id, t));
        const railOf = (t: string) => railsOf.get(rootOf(t));

        const u1Root = rootOf("U1");
        const v1Root = rootOf("V1");
        const w1Root = rootOf("W1");
        const u2Root = rootOf("U2");
        const v2Root = rootOf("V2");
        const w2Root = rootOf("W2");

        const topRoots = [u1Root, v1Root, w1Root];
        const topRootsAreSeparate = new Set(topRoots).size === 3;

        const u1Rails = railOf("U1");
        const v1Rails = railOf("V1");
        const w1Rails = railOf("W1");

        const pU = singlePhase(u1Rails);
        const pV = singlePhase(v1Rails);
        const pW = singlePhase(w1Rails);

        const cleanThreePhases =
          !!pU &&
          !!pV &&
          !!pW &&
          new Set([pU, pV, pW]).size === 3 &&
          topRootsAreSeparate;

        const found = new Set<Rail>();
        [u1Rails, v1Rails, w1Rails].forEach((s) =>
          PHASES.forEach((p) => s?.has(p) && found.add(p)),
        );

        // STAR: U2 + V2 + W2 كلها نقطة واحدة،
        // ونقطة النجمة لا تكون موصولة مباشرة بأي فازة.
        const starRoot = u2Root;
        const starPointRails = railsOf.get(starRoot);
        const starHasPhase = hasAny(starPointRails, PHASES);

        const starConnected =
          u2Root === v2Root &&
          v2Root === w2Root &&
          !topRoots.includes(starRoot);

        const starValid = starConnected && !starHasPhase;

        // DELTA القياسي:
        // U1 مع W2
        // V1 مع U2
        // W1 مع V2
        const deltaValid =
          topRootsAreSeparate &&
          u1Root === w2Root &&
          v1Root === u2Root &&
          w1Root === v2Root;

        if (cleanThreePhases && (starValid || deltaValid)) {
          running.add(c.id);

          if (starValid) motorConnection.set(c.id, "star");
          else motorConnection.set(c.id, "delta");

          const dir = directionFromThreePhase(u1Rails, v1Rails, w1Rails);
          if (dir) motorDirection.set(c.id, dir);
        } else {
          if (found.size > 0 && found.size < 3) {
            issues.push({
              level: "error",
              compId: c.id,
              message: `${c.label}: فقدان فازة – يجب وصول ثلاث فازات مختلفة إلى U1/V1/W1.`,
            });
          } else if (found.size === 3 && !cleanThreePhases) {
            issues.push({
              level: "error",
              compId: c.id,
              message: `${c.label}: ترتيب التغذية غير صالح أو يوجد تداخل بين الفازات على أطراف U1/V1/W1.`,
            });
          }

          // لا نبلغ عن ربط STAR/DELTA كخطأ والمحرك مفصول تماماً عن الفازات.
          // عند وصول أي فازة يبدأ الفحص الحقيقي للربط، وهذا يمنع ظهور خطأ كاذب
          // في المثال الجاهز قبل ضغط START.
          if (!starValid && !deltaValid && found.size > 0) {
            issues.push({
              level: "error",
              compId: c.id,
              message:
                `${c.label}: ربط ملفات المحرك غير صحيح. ` +
                `STAR: اربط U2/V2/W2 معاً. ` +
                `DELTA: اربط U1-W2 و V1-U2 و W1-V2.`,
            });
          }
        }
      } else {
        // =====================================================
        // بقية الأحمال
        // =====================================================
        const un = spec.load.terminals.filter((t) => !connected.has(key(c.id, t)));
        if (un.length)
          issues.push({
            level: "warn",
            compId: c.id,
            message: `${c.label}: الأطراف ${un.join(" ، ")} غير مربوطة.`,
          });

        const rails = spec.load.terminals.map((t) =>
          railsOf.get(dsu.find(key(c.id, t))),
        );

        if (spec.load.kind === "3ph") {
          const found = new Set<Rail>();
          rails.forEach((s) => PHASES.forEach((p) => s?.has(p) && found.add(p)));

          if (found.size === 3) {
            running.add(c.id);

            // اتجاه الدوران للمحرك الثلاثي القديم والمضخة الثلاثية
            if (c.type === "motor_3ph" || c.type === "pump_3ph") {
              const dir = directionFromThreePhase(rails[0], rails[1], rails[2]);
              if (dir) motorDirection.set(c.id, dir);
            }
          } else if (found.size > 0) {
            issues.push({
              level: "error",
              compId: c.id,
              message: `${c.label}: لا تصله الفازات الثلاث (الموجود: ${[...found].join(", ")}) – فقدان فازة.`,
            });
          }
        } else if (spec.load.kind === "dc") {
          const dcDirection = dcDirectionFromPair(rails[0], rails[1]);

          if (dcDirection) {
            running.add(c.id);

            // فان DC يعكس اتجاهه مباشرة عند عكس القطبية.
            if (c.type === "fan_dc") {
              motorDirection.set(c.id, dcDirection);
            }
          } else if (
            rails.some((r) => r?.has("C+") || r?.has("C-"))
          ) {
            issues.push({
              level: "error",
              compId: c.id,
              message: `${c.label}: يحتاج تغذية 24V DC صحيحة بين C+ و C-.`,
            });
          }
        } else {
          // كل حمل Single Phase / أحادي الطور الموجود حالياً AC فقط:
          // فازة واحدة + N. لا يعمل على فازتين ولا على 24V DC.
          if (pairPoweredAC(rails[0], rails[1])) running.add(c.id);
          else if (
            rails.some((r) => hasAny(r, PHASES)) &&
            !rails.some((r) => r?.has("N"))
          )
            issues.push({
              level: "error",
              compId: c.id,
              message: `${c.label}: النيوترال مفقود – يجب ربط فازة واحدة + N.`,
            });
        }
      }
    }

    if (c.type === "overload" && c.state.tripped)
      issues.push({
        level: "error",
        compId: c.id,
        message: `${c.label}: الأوفرلود مفصول (Trip) – الدائرة متوقفة حسب ربط 95/96.`,
      });
    if (c.type === "fuse" && c.state.tripped)
      issues.push({ level: "error", compId: c.id, message: `${c.label}: الفيوز محروق.` });
    if (c.type === "estop" && c.state.closed)
      issues.push({
        level: "error",
        compId: c.id,
        message: `${c.label}: Emergency Stop مضغوط – الدائرة مقفولة.`,
      });
    if (
      (c.type.startsWith("mcb") || c.type.startsWith("mccb") || c.type === "rccb" || c.type === "rcbo") &&
      !c.state.closed
    )
      issues.push({ level: "warn", compId: c.id, message: `${c.label}: القاطع مفتوح (OFF).` });
    if (
      (c.type === "phase_failure" || c.type === "voltage_monitor") &&
      c.state.protectionFault
    )
      issues.push({
        level: "error",
        compId: c.id,
        message: `${c.label}: الحماية مفصولة – فقد فاز/خطأ ترتيب أو الفولتية خارج الحدود المضبوطة.`,
      });
  }

  return {
    nodeOf: new Map(
      comps.flatMap((c) =>
        (SPECS[c.type]?.terminals ?? []).map(
          (t) => [key(c.id, t.id), dsu.find(key(c.id, t.id))] as [string, string],
        ),
      ),
    ),
    railsOf,
    energized,
    coilPowered,
    running,
    liveTerminals,
    liveNodes,
    protectionStatus,
    motorDirection,
    motorConnection,
    issues,
  };
}

export const termKey = key;
